<script>
import CourseDetailComponent from '../../features/details/CourseDetailComponent';
import CourseSectionsView from '../boxes/CourseSectionsView';
import CourseInstructorsView from '../boxes/CourseInstructorsView';

export default {
  name: 'CourseDetailView',
  components: {
    'course-sections-view': CourseSectionsView,
    'course-instructors-view': CourseInstructorsView,
  },
  extends: CourseDetailComponent,
};
</script>

<template>
  <div class="course_detail_page">
    <main-menu-student-view />

    <div class="wrapper">
      <div class="sa4d25">
        <div class="container-fluid">
          <!-- Back Bar / Navigation -->
          <div class="course_back_bar">
            <button
              class="course_back_btn"
              @click="$router.push('/estudiante/cursos')"
            >
              <i class="uil uil-arrow-left" />
              <span>Volver a Cursos</span>
            </button>

            <div
              v-if="courseDetails && courseDetails.tags"
              class="course_header_tags"
            >
              <span
                v-for="(tag, idx) in getTagsList(courseDetails.tags)"
                :key="idx"
                class="course_tag_badge"
              >
                {{ tag }}
              </span>
            </div>
          </div>

          <!-- Course Details Found -->
          <template v-if="courseDetails">
            <!-- Hero Card -->
            <div class="course_hero_card">
              <div class="row align-items-center">
                <!-- Preview / Media Column -->
                <div class="col-xl-5 col-lg-5 col-md-12">
                  <div class="course_media_container">
                    <div
                      class="course_media_box"
                      :class="{ 'has_video': hasPromotionalVideo() }"
                      @click="hasPromotionalVideo() ? onShowPromotionalVideo() : null"
                    >
                      <img
                        :src="getCoverImage(courseDetails.coverImageUrl)"
                        :alt="courseDetails.title"
                        class="course_cover_img"
                      >
                      <div
                        v-if="hasPromotionalVideo()"
                        class="course_media_overlay"
                      >
                        <div class="play_pulse_badge">
                          <i class="uil uil-play" />
                        </div>
                        <span class="play_text">Ver Video Promocional</span>
                      </div>
                    </div>

                    <button
                      class="course_fav_btn"
                      :class="{ 'is_fav': isFavorite }"
                      @click="onDoFavorite"
                    >
                      <i
                        class="uil uil-heart"
                        :class="{ 'active': isFavorite }"
                      />
                      <span>{{ isFavorite ? 'En tus Favoritos' : 'Guardar en Favoritos' }}</span>
                    </button>
                  </div>
                </div>

                <!-- Info & CTA Column -->
                <div class="col-xl-7 col-lg-7 col-md-12">
                  <div class="course_hero_content">
                    <h1 class="course_main_title">
                      {{ courseDetails.title }}
                    </h1>
                    <p class="course_main_desc">
                      {{ courseDetails.description }}
                    </p>

                    <div class="course_meta_chips_row">
                      <span class="hero_chip">
                        <i class="uil uil-users-alt" />
                        <strong>{{ courseDetails.totalEnrolled }}</strong> Estudiante(s)
                      </span>
                      <span class="hero_chip">
                        <i class="uil uil-layer-group" />
                        <strong>{{ courseDetails.getTotalSections() }}</strong> Secciones
                      </span>
                      <span
                        v-if="courseDetails.lastUpdate"
                        class="hero_chip"
                      >
                        <i class="uil uil-history" />
                        Actualizado {{ courseDetails.lastUpdate }}
                      </span>
                    </div>

                    <div class="course_hero_cta_row">
                      <el-button
                        type="primary"
                        size="large"
                        class="course_start_btn"
                        @click="onGoToCourse(courseDetails)"
                      >
                        <i class="uil uil-play-circle" />
                        <span>Ir al Curso</span>
                      </el-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Author & Social Bar -->
            <div class="course_meta_strip">
              <div
                v-if="courseDetails.author"
                class="author_identity_box"
              >
                <img
                  :src="getProfileImage(courseDetails.author)"
                  :alt="courseDetails.author.firstName"
                  class="author_avatar"
                >
                <div class="author_details">
                  <span class="author_name">
                    {{ courseDetails.author.firstName }} {{ courseDetails.author.lastName }}
                  </span>
                  <span class="author_badge">
                    <i class="uil uil-award" /> Docente Titular
                  </span>
                </div>
              </div>

              <div class="social_stats_box">
                <div
                  class="social_stat_item"
                  title="Visitas totales"
                >
                  <i class="uil uil-eye" />
                  <span>{{ totalViewed }}</span>
                </div>
                <button
                  class="social_btn"
                  :class="{ 'is_active': isLiked }"
                  title="Me gusta"
                  @click="onDoLike"
                >
                  <i class="uil uil-thumbs-up" />
                  <span>{{ totalLiked }}</span>
                </button>
                <button
                  class="social_btn"
                  :class="{ 'is_active': isDisliked }"
                  title="No me gusta"
                  @click="onDoDislike"
                >
                  <i class="uil uil-thumbs-down" />
                  <span>{{ totalDisliked }}</span>
                </button>
              </div>
            </div>

            <!-- Tabs Navigation -->
            <div class="course_tabs_container">
              <div class="course_tabs_nav">
                <button
                  class="course_tab_btn"
                  :class="{ 'active': activeTab === 'about' }"
                  @click="activeTab = 'about'"
                >
                  <i class="uil uil-info-circle" />
                  <span>Acerca del Curso</span>
                </button>
                <button
                  class="course_tab_btn"
                  :class="{ 'active': activeTab === 'content' }"
                  @click="activeTab = 'content'"
                >
                  <i class="uil uil-list-ui-alt" />
                  <span>Contenido del Curso</span>
                  <span class="tab_count_badge">{{ courseDetails.getTotalSections() }}</span>
                </button>
                <button
                  class="course_tab_btn"
                  :class="{ 'active': activeTab === 'instructors' }"
                  @click="activeTab = 'instructors'"
                >
                  <i class="uil uil-graduation-cap" />
                  <span>Profesores</span>
                  <span
                    v-if="courseDetails.instructors"
                    class="tab_count_badge"
                  >{{ courseDetails.instructors.length }}</span>
                </button>
              </div>

              <!-- Tab 1: Acerca del Curso -->
              <div
                v-show="activeTab === 'about'"
                class="course_tab_card"
              >
                <div class="tab_card_header">
                  <div class="header_left">
                    <div class="header_icon_circle">
                      <i class="uil uil-document-info" />
                    </div>
                    <div>
                      <h3 class="header_title">
                        Acerca del Curso
                      </h3>
                      <p class="header_subtitle">
                        Información pedagógica, objetivos y consideraciones generales
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  v-if="courseDetails.about"
                  class="course_about_body"
                  v-html="courseDetails.about"
                />
                <div
                  v-else
                  class="course_empty_tab"
                >
                  <i class="uil uil-info-circle" />
                  <p>No hay información adicional disponible para este curso.</p>
                </div>
              </div>

              <!-- Tab 2: Contenido del Curso -->
              <div
                v-show="activeTab === 'content'"
                class="course_tab_card"
              >
                <div class="tab_card_header flex-wrap gap-3">
                  <div class="header_left">
                    <div class="header_icon_circle">
                      <i class="uil uil-books" />
                    </div>
                    <div>
                      <h3 class="header_title">
                        Estructura y Temario
                      </h3>
                      <p class="header_subtitle">
                        Revisa los módulos, lecciones y actividades incluidas
                      </p>
                    </div>
                  </div>

                  <div class="header_right_controls">
                    <div
                      v-if="careers && careers.length"
                      class="specialty_filter_group"
                    >
                      <label class="filter_label">Especialidad:</label>
                      <el-select
                        v-model="courseDetails.specialty"
                        placeholder="Filtrar especialidad..."
                        clearable
                        class="specialty_select"
                        @change="onChangeCareerSelected"
                      >
                        <el-option
                          v-for="item in careers"
                          :key="item.id"
                          :label="item.name"
                          :value="item.id"
                        />
                      </el-select>
                    </div>

                    <span class="sections_total_pill">
                      <i class="uil uil-folder-check" />
                      {{ courseDetails.getTotalSections() }} Secciones
                    </span>
                  </div>
                </div>

                <div class="course_content_body">
                  <course-sections-view
                    v-loading="isCourseSectionsLoading"
                    :course-details="courseDetails"
                  />
                </div>
              </div>

              <!-- Tab 3: Profesores -->
              <div
                v-show="activeTab === 'instructors'"
                class="course_tab_card"
              >
                <div class="tab_card_header">
                  <div class="header_left">
                    <div class="header_icon_circle">
                      <i class="uil uil-graduation-cap" />
                    </div>
                    <div>
                      <h3 class="header_title">
                        Equipo Docente
                      </h3>
                      <p class="header_subtitle">
                        Conoce a los profesores e instructores encargados de esta materia
                      </p>
                    </div>
                  </div>
                </div>

                <div class="course_instructors_body">
                  <course-instructors-view :course-details="courseDetails" />
                </div>
              </div>
            </div>
          </template>

          <!-- Empty State when course not found -->
          <div
            v-else-if="!fullLoader && !courseDetails"
            class="course_not_found_card"
          >
            <div class="not_found_icon">
              <i class="uil uil-file-search-alt" />
            </div>
            <h2>Curso no encontrado</h2>
            <p>El curso que estás intentando consultar no existe o no se encuentra disponible actualmente.</p>
            <el-button
              type="primary"
              class="courses_search_btn"
              @click="$router.push('/estudiante/cursos')"
            >
              <i class="uil uil-arrow-left" /> Volver al catálogo de cursos
            </el-button>
          </div>
        </div>
      </div>


      <modal-course-promotional-video-view
        v-if="modalItemCoursePromotionalVideo.isVisible"
        :conf="modalItemCoursePromotionalVideo"
        :form-data="courseDetails"
      />
    </div>

    <div
      v-loading.fullscreen.lock="fullLoader"
      element-loading-text="Cargando detalles del curso..."
      element-loading-background="rgba(244, 247, 251, 0.75)"
    />
  </div>
</template>
